import { useEffect, useState } from "react";
import { useQuizStore } from "../state/store";
import { resolveTheme, subscribeToSystemTheme } from "../lib/theme";

export type BrandVariant = "icon" | "horizontal" | "square";

interface BrandMarkProps {
  variant: BrandVariant;
  /** Square size in px (only used when `variant === "icon"` or `"square"`). For
   *  the horizontal wordmark, use `className` to set width or height. */
  size?: number;
  className?: string;
  /** Override the aria-label. Defaults to "PPLearning" — set to empty string
   *  when the wordmark sits next to a visible "PPLearning" text label. */
  ariaLabel?: string;
}

interface AssetSet {
  light: string;
  dark: string;
  /** Native aspect ratio (width / height). For predictable layout when only
   *  one of width/height is set. */
  aspect: number;
}

const ASSETS: Record<BrandVariant, AssetSet> = {
  icon: {
    light: "/brand/icon-only-light.png",
    dark: "/brand/icon-only-dark.png",
    aspect: 1536 / 1024,
  },
  horizontal: {
    light: "/brand/wordmark-horizontal-light.png",
    dark: "/brand/wordmark-horizontal-dark.png",
    aspect: 1654 / 512,
  },
  square: {
    light: "/brand/wordmark-square-light.png",
    dark: "/brand/wordmark-square-dark.png",
    aspect: 1,
  },
};

/**
 * Theme-aware brand mark — renders the right asset variant for the user's
 * current effective theme (system / light / dark). Subscribes to the system
 * theme so the asset swaps live when the OS theme changes and `theme=system`.
 *
 * Use in headers / auth / completion / settings. Never on the quiz card.
 */
export function BrandMark({
  variant,
  size,
  className,
  ariaLabel = "PPLearning",
}: BrandMarkProps) {
  const pref = useQuizStore((s) => s.settings.theme);
  const [resolved, setResolved] = useState<"dark" | "light">(() => resolveTheme(pref));

  useEffect(() => {
    setResolved(resolveTheme(pref));
    return subscribeToSystemTheme(
      () => pref,
      () => setResolved(resolveTheme(pref)),
    );
  }, [pref]);

  const asset = ASSETS[variant];
  const src = resolved === "dark" ? asset.dark : asset.light;

  const sizeProps =
    variant === "horizontal"
      ? {} // sized via className (h-* or w-*)
      : size !== undefined
      ? { width: size, height: variant === "icon" ? size : size }
      : {};

  // Inline `display` here would override Tailwind's `md:hidden` /
  // `hidden md:inline-block` responsive classes, causing both variants
  // to render at the same time (caught in the header on 2026-05-14).
  // `object-fit` is the only style we need to set inline.
  return (
    <img
      src={src}
      alt={ariaLabel}
      role={ariaLabel ? "img" : "presentation"}
      aria-hidden={ariaLabel ? undefined : true}
      className={className}
      data-testid={`brand-mark-${variant}`}
      data-theme={resolved}
      {...sizeProps}
      style={{ objectFit: "contain" }}
    />
  );
}
